import { useEffect, useState, FC, useCallback } from 'react';
import { Dialog, DialogContent, DialogActions, DialogTitle, IconButton, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { normalizeString } from 'utils/Validations';
import { PayRuleDB } from 'repository/timekeeper/@types/PayRuleModel';
import { TimekeeperService } from 'services/TimekeeperService';
import { generateRow, FixedRule, InterpretationRule, WorkRule, HolidayRule, Holiday, AssignmentRow, PayRuleFormData } from './common';
import { HolidayTabs } from 'repository/timekeeper/@types/HolidayModel';
import { PayRulesForm } from './PayRulesForm';
import { PaperComponent } from 'shared/ui/draggable/Draggable';

interface InfoPayRulesProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    rows: PayRuleDB[];
    cleanSelected: () => void;
    rowsSelected: PayRuleDB[];
    mode: 'add' | 'edit' | 'info' | 'delete';
}

const timekeeperService = new TimekeeperService();

// Main Component
const InfoPayRules: FC<InfoPayRulesProps> = ({ open, setOpen, rows, cleanSelected, rowsSelected, mode }) => {
    const { t } = useTranslation();
    const [selectedTab, setSelectedTab] = useState(0);
    const [formData, setFormData] = useState<PayRuleFormData>({
        name: '',
        description: '',
        calculateProjectedTotals: false,
        tryPlannedTime: false,
        fixedRuleSelected: null,
        interpretationRuleSelected: null,
        workRuleSelected: null,
        creditHolidayRuleSelected: null,
        holidaysSelected: [],
        assignment: [generateRow(0)],
    });

    // Rule options
    const [fixedRules, setFixedRules] = useState<FixedRule[]>([]);
    const [interpretationRules, setInterpretationRules] = useState<InterpretationRule[]>([]);
    const [workRules, setWorkRules] = useState<WorkRule[]>([]);
    const [creditHolidayRules, setCreditHolidayRules] = useState<HolidayRule[]>([]);

    // Holidays
    const [holidays, setHolidays] = useState<Holiday[]>([]);
    const [loadingWorkRules, setLoadingWorkRules] = useState(false);

    // Memoized values
    const existName = useCallback(
        (name: string) => {
            const normalizedInput = normalizeString(name);
            return rows.some((row) => row.pay_rule_id !== rowsSelected[0]?.pay_rule_id && normalizeString(row.name) === normalizedInput);
        },
        [rows, rowsSelected],
    );

    const closeForm = useCallback(() => {
        setFormData({
            name: '',
            description: '',
            calculateProjectedTotals: false,
            tryPlannedTime: false,
            fixedRuleSelected: null,
            interpretationRuleSelected: null,
            workRuleSelected: null,
            creditHolidayRuleSelected: null,
            holidaysSelected: [],
            assignment: [generateRow(0)],
        });
        setFixedRules([]);
        setInterpretationRules([]);
        setWorkRules([]);
        setCreditHolidayRules([]);
        setHolidays([]);
        setSelectedTab(0);
        setOpen(false);
        cleanSelected();
    }, [setOpen, cleanSelected]);

    // Effects
    useEffect(() => {
        if (!open || !rowsSelected || rowsSelected.length === 0) return;

        const selectedPayRule = rowsSelected[0];

        const fetchData = async () => {
            setLoadingWorkRules(true);
            try {
                const [fixedRulesData, interpretationRulesData, workRulesData, creditHolidayRulesData, holidaysData] = await Promise.allSettled([
                    timekeeperService.fixed_rule.getFixedRules(),
                    timekeeperService.interpretation_rules.getAll(),
                    timekeeperService.work_rules.getWorkRules(),
                    timekeeperService.credit_holiday_rule.getCreditHolidayRules(),
                    timekeeperService.holiday.getHolidays(),
                ]);

                if (fixedRulesData.status === 'fulfilled') {
                    const rules = fixedRulesData.value || [];
                    const orderedRules = rules.sort((a: FixedRule, b: FixedRule) => a.name.localeCompare(b.name));
                    setFixedRules(orderedRules);

                    const selectedRule = orderedRules.find((r: FixedRule) => r.fixed_rule_id === selectedPayRule.fixed_rule_id);
                    if (selectedRule) {
                        setFormData((prev) => ({ ...prev, fixedRuleSelected: selectedRule }));
                    }
                }

                if (interpretationRulesData.status === 'fulfilled') {
                    const rules = interpretationRulesData.value || [];
                    const orderedRules = rules.sort((a: InterpretationRule, b: InterpretationRule) => a.name.localeCompare(b.name));
                    setInterpretationRules(orderedRules);

                    const selectedRule = orderedRules.find((r: InterpretationRule) => r.interpretation_rule_id === selectedPayRule.interpretation_rule_id);
                    if (selectedRule) {
                        setFormData((prev) => ({ ...prev, interpretationRuleSelected: selectedRule }));
                    }
                }

                if (workRulesData.status === 'fulfilled') {
                    const rules = workRulesData.value || [];
                    const orderedRules = rules.sort((a: WorkRule, b: WorkRule) => a.name.localeCompare(b.name));
                    setWorkRules(orderedRules);

                    const selectedRule = orderedRules.find((r: WorkRule) => r.work_rule_id === selectedPayRule.work_rule_default_id);
                    if (selectedRule) {
                        setFormData((prev) => ({ ...prev, workRuleSelected: selectedRule }));
                    }
                }

                if (creditHolidayRulesData.status === 'fulfilled') {
                    const rules = creditHolidayRulesData.value || [];
                    const orderedRules = rules.sort((a: HolidayRule, b: HolidayRule) => a.name.localeCompare(b.name));
                    setCreditHolidayRules(orderedRules);

                    if (selectedPayRule.credit_holiday_default_id) {
                        const selectedRule = orderedRules.find((r: HolidayRule) => r.holiday_rule_id === selectedPayRule.credit_holiday_default_id);
                        if (selectedRule) {
                            setFormData((prev) => ({ ...prev, creditHolidayRuleSelected: selectedRule }));
                        }
                    }
                }

                if (holidaysData.status === 'fulfilled') {
                    const holidayRows: HolidayTabs[] = holidaysData.value.map((holiday: { holiday_id: number; name: string; country?: string }) => ({
                        id: holiday.holiday_id,
                        name: holiday.name,
                        option: false,
                        holiday_credit_rule: '',
                        country: holiday.country || '',
                    }));
                    const orderedHolidayRows = holidayRows.sort((a, b) => a.name.localeCompare(b.name));
                    setHolidays(orderedHolidayRows);
                }

                // Precargar datos desde selectedPayRule (primer elemento)
                const assignmentRows: AssignmentRow[] =
                    selectedPayRule.asignation && selectedPayRule.asignation.length > 0
                        ? selectedPayRule.asignation.map((item, index) => ({
                              id: index,
                              order: item.order,
                              pay_rule_id: item.pay_rule_id,
                              work_rule_id: item.work_rule_id,
                              start_hour: item.start_hour || '',
                              end_hour: item.end_hour || '',
                              min_shift: item.min_shift || '',
                              max_shift: item.max_shift || '',
                              days_week: new Set(item.days_week || []),
                              planned: item.planned,
                              unplanned: item.unplanned,
                              pay_rule_r_work_rule_id: item.pay_rule_r_work_rule_id,
                          }))
                        : [generateRow(0)];

                const holidaysIds = Array.isArray(selectedPayRule.holidays) ? selectedPayRule.holidays.map((h: any) => (typeof h === 'number' ? h : h.holiday_id)) : [];

                setFormData((prev) => ({
                    ...prev,
                    name: selectedPayRule.name,
                    description: selectedPayRule.description,
                    calculateProjectedTotals: selectedPayRule.calculate_projected_totals,
                    tryPlannedTime: selectedPayRule.treat_planned_hour,
                    holidaysSelected: holidaysIds,
                    assignment: assignmentRows,
                }));
            } catch (error) {
                console.error('Error fetching rules:', error);
            } finally {
                setLoadingWorkRules(false);
            }
        };

        fetchData();
    }, [open, rowsSelected]);

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') return;
                closeForm();
            }}
            maxWidth={selectedTab === 0 ? 'sm' : selectedTab === 1 ? 'md' : 'xl'}
            fullWidth
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {t('settings.pay_rules.dialog.titles.edit')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={closeForm}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent dividers>
                <PayRulesForm
                    formData={formData}
                    setFormData={setFormData}
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                    ruleOptions={{
                        fixedRules,
                        interpretationRules,
                        workRules,
                        creditHolidayRules,
                    }}
                    holidays={holidays}
                    loadingWorkRules={loadingWorkRules}
                    existName={existName}
                    mode={mode}
                />
            </DialogContent>

            <DialogActions>
                <Button variant="contained" onClick={closeForm}>
                    {t('general.buttons.close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export { InfoPayRules };
